import React, { Fragment, useEffect, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import Heading from '@atlaskit/heading';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import Link from '@atlaskit/link';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Grid } from '@atlaskit/primitives';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onNonGenerativeAIAnswerResultShown } from '../../../ai-answer-dialog/analytics';
import { type NonGenerativeAIAnswerResultShownProps } from '../../../ai-answer-dialog/types';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { useTenantContext } from '../../../common/utils/use-tenant-context';
import { AnswerCardType } from '../../../constants';
import { onNonGenerativeKnowledgeCardLinkClick } from '../../../knowledge-cards/analytics';
import { type KnowledgeDiscoveryTopicByIdResult } from '../../../knowledge-cards/gql/hydrate-topic-card/gql';
import { KnowledgeCardsRow } from '../../../knowledge-cards/knowledge-cards-row';
import { KnowledgeCardsRowType } from '../../../knowledge-cards/types';
import { sha256Hash } from '../../../utils';
import { AIDisclaimerAndFeedback } from '../../common/ui/ai-disclaimer-and-feedback';
import { SeparatorLine } from '../../common/ui/separator-line';
import { KnowledgeCard } from '../base-components/KnowledgeCard';

const messages = defineMessages({
	topic_knowledge_card_why_tooltip: {
		id: 'search_ai.topic_knowledge_card.why_tooltip',
		defaultMessage: 'We think your search might be related to {topicName}',
		description: 'Text for tooltip explaining why user is seeing topic knowledge card',
	},
	topic_knowledge_card_people_heading: {
		id: 'search_ai.topic_knowledge_card.people_heading',
		defaultMessage: 'People',
		description: 'Heading for people section in topic knowledge card',
	},
	topic_knowledge_card_people_view_all_cta: {
		id: 'search_ai.topic_knowledge_card.people_view_all_cta',
		defaultMessage: 'View all',
		description: 'View all action text for people section in topic knowledge card',
	},
});

interface Person {
	accountId: string;
	firstName: string;
	lastName: string;
	role: string;
	avatarUrl: string;
}

export interface Topic {
	name: string;
	url: string;
	description: string;
	people: Person[];
}

export const TopicKnowledgeCard = ({
	onFMPReached,
}: {
	onFMPReached?: () => void;
}): React.JSX.Element | null => {
	const { commonAttributes } = useAIAnswerContext();
	const { answerString, brand, cloudId, query, searchSessionId } = commonAttributes;

	const { formatMessage } = useIntl();

	const { trackAIInteractionInit } = useAIEventsInstrumentation();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const [fireAnalyticsEvent] = useAnalytics();

	const {
		tenantContext: { cloudUrl },
	} = useTenantContext(cloudId);

	const analyticsAttributes = useMemo<NonGenerativeAIAnswerResultShownProps>(
		() => ({
			searchSessionId,
			queryHash: sha256Hash(query),
			brand,
			source: 'topic_knowledge_card',
			query,
			answerString,
			answerCardType: AnswerCardType.TOPIC,
		}),
		[searchSessionId, query, brand, answerString],
	);

	useEffect(() => {
		onFMPReached?.();
		trackAIInteractionInit();
		fireSAINUsageEvent({ commonAttributes });
		fireAnalyticsEvent(onNonGenerativeAIAnswerResultShown(analyticsAttributes));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleTopicClick = () => {
		fireSAINUsageEvent({ metCriteria: SainAction.TOPIC_KNOWLEDGE_CARD_CLICKED, commonAttributes });
		fireAnalyticsEvent(onNonGenerativeKnowledgeCardLinkClick(analyticsAttributes));
	};

	const topicData = JSON.parse(answerString) as KnowledgeDiscoveryTopicByIdResult;
	const relatedUsers = topicData.users;

	const topicUrl = cloudUrl ? `${cloudUrl}/wiki/topics/${topicData.topic.id}` : '';
	const getUserUrl = (userId: string) => {
		return cloudUrl ? `${cloudUrl}/wiki/people/${userId}` : '';
	};

	// if reached here, this means we query intent as topic
	// but topic query did not find topic for id returned by query intent
	if (topicData.topic.message) {
		return null;
	}

	return (
		<KnowledgeCard name="smart-answers-topic-knowledge-card" size="full">
			<Stack space="space.150" grow="fill">
				<Inline alignBlock="center" space="space.075">
					<Link href={topicUrl} appearance="subtle" onClick={handleTopicClick}>
						<Text size="large" color="color.link" weight="semibold">
							{topicData.topic.name}
						</Text>
					</Link>
					<Tooltip
						content={formatMessage(messages.topic_knowledge_card_why_tooltip, {
							topicName: topicData.topic.name,
						})}
					>
						<StatusInformationIcon label="" size="small" color={token('color.text.subtlest')} />
					</Tooltip>
				</Inline>
				<Text color="color.text.subtle">{topicData.topic.description}</Text>

				{relatedUsers.nodes && relatedUsers.nodes.length > 0 && (
					<Fragment>
						<SeparatorLine />
						<Inline spread="space-between" alignBlock="center">
							<Heading as="h6" size="small">
								{formatMessage(messages.topic_knowledge_card_people_heading)}
							</Heading>
							{relatedUsers.nodes.length > 4 && (
								<Link
									href={topicUrl}
									appearance="subtle"
									onClick={handleTopicClick}
									testId="view-all-people-cta"
								>
									<Text size="small" color="color.text.subtlest">
										{formatMessage(messages.topic_knowledge_card_people_view_all_cta)}
									</Text>
								</Link>
							)}
						</Inline>

						<Grid templateColumns="1fr 1fr" gap="space.100">
							{relatedUsers.nodes.slice(0, 4).map(
								({ user }) =>
									user && (
										<Link
											href={getUserUrl(user.accountId)}
											appearance="subtle"
											key={user.id}
											testId="topic-person-row"
										>
											<KnowledgeCardsRow
												primaryText={user.name}
												secondaryText={user.extendedProfile?.jobTitle ?? ''}
												pictureUrl={user.picture}
												type={KnowledgeCardsRowType.PERSON}
											/>
										</Link>
									),
							)}
						</Grid>
					</Fragment>
				)}

				<AIDisclaimerAndFeedback showFeedbackButtons={true} />
			</Stack>
		</KnowledgeCard>
	);
};
